// webauthn-prf-browser.js
// The actual WebAuthn ceremony for the PRF extension — creating a
// hardware-backed credential, and later re-deriving the SAME secret
// from it (gated by a fresh biometric/platform check each time) for use
// as encryption key material (see identity-crypto-browser.js, which
// handles turning that secret into an actual AES key — this file's job
// stops at getting the raw secret out of the authenticator).
//
// Real, important limitation of this whole approach, worth stating
// plainly: PRF support varies by platform and authenticator. As of
// this writing, synced passkeys (iCloud Keychain via Face ID/Touch ID,
// Google Password Manager on Android/Chrome/Edge) have strong support;
// Windows Hello gained support via a February 2026 update; a documented
// gap remains for external/roaming authenticators (YubiKeys) specifically
// on iOS/iPadOS, where Apple's WebAuthn implementation doesn't pass PRF
// extension data through to them at all. isPrfSupported() below can only
// tell you "no" with certainty ahead of time (checking for the base
// WebAuthn API) — confirming "yes" requires actually attempting
// setupPrfCredential() and checking whether the ceremony's result
// includes a usable PRF output, since there's no synchronous
// capability flag for this specific extension.
//
// Cannot be tested without a real authenticator (Face ID, Touch ID,
// Windows Hello, or a hardware key) — no meaningful software stand-in
// exists for what this file does; a stub would just test itself.
// identity-crypto-browser.js (the AES-GCM/HKDF work downstream of
// whatever secret this produces) IS fully tested with real Web Crypto —
// see tests/browser-identity-crypto-test.mjs. This file needs a real
// device to verify.

const RP_NAME = 'Station'
const PRF_SALT = new TextEncoder().encode('station-identity-prf-v1')

/** Baseline check only — see module comment on why this can't confirm PRF specifically. */
export function isWebAuthnAvailable () {
  return typeof window !== 'undefined' &&
    typeof window.PublicKeyCredential !== 'undefined' &&
    typeof navigator?.credentials?.create === 'function'
}

/**
 * Create a new platform credential and attempt to derive a PRF secret
 * from it in the same ceremony where possible. Some authenticators only
 * report PRF availability on create() but don't return an actual value
 * until a follow-up get() — this handles both cases, doing that
 * follow-up automatically when needed.
 *
 * @param {string} identityFingerprint - used as the credential's
 *   user.id/displayName so this is recognizable in the OS's passkey
 *   list, not because it's sensitive (it's already meant to be shared
 *   for out-of-band verification).
 * @returns {Promise<{credentialId: Uint8Array, prfSecret: Uint8Array} | null>}
 *   null means this authenticator doesn't support PRF — caller should
 *   fall back to the passphrase path.
 */
export async function setupPrfCredential (identityFingerprint) {
  if (!isWebAuthnAvailable()) return null

  try {
    const userId = crypto.getRandomValues(new Uint8Array(16))
    const challenge = crypto.getRandomValues(new Uint8Array(32))

    const credential = await navigator.credentials.create({
      publicKey: {
        rp: { name: RP_NAME },
        user: { id: userId, name: identityFingerprint, displayName: identityFingerprint },
        challenge,
        pubKeyCredParams: [
          { type: 'public-key', alg: -7 }, // ES256
          { type: 'public-key', alg: -257 } // RS256, wider hardware support
        ],
        authenticatorSelection: { userVerification: 'required' }, // require the actual biometric/PIN check, not just presence
        extensions: { prf: { eval: { first: PRF_SALT } } }
      }
    })

    if (!credential) return null

    const extResults = credential.getClientExtensionResults()
    const credentialId = new Uint8Array(credential.rawId)

    // Some authenticators return the PRF value directly on create().
    if (extResults.prf?.results?.first) {
      return { credentialId, prfSecret: new Uint8Array(extResults.prf.results.first) }
    }

    // Others only confirm support on create() and require a follow-up
    // get() to actually produce a value.
    if (extResults.prf?.enabled) {
      const prfSecret = await getPrfSecret(credentialId)
      return prfSecret ? { credentialId, prfSecret } : null
    }

    // No PRF support signaled at all — caller falls back to passphrase.
    return null
  } catch {
    // A real, expected failure mode, not just a theoretical one: WebAuthn
    // has real origin restrictions (a raw IP address like 127.0.0.1
    // generally isn't a valid WebAuthn origin — only 'localhost' or a
    // real HTTPS domain qualify), and the user can also simply cancel
    // the biometric prompt. Either way, this function's whole contract
    // is "null means fall back to passphrase" — a thrown exception here
    // would violate that silently (an unhandled rejection with no
    // visible error, confirmed directly: this crashed boot() entirely
    // on 127.0.0.1 before this fix, leaving the identity panel stuck at
    // "generating..." forever with nothing shown to the user).
    return null
  }
}

/**
 * Re-derive the same PRF secret from an existing credential — this is
 * what runs on every subsequent unlock, triggering a fresh biometric/
 * platform check each time. Deterministic: same credential + same salt
 * always produces the same secret, which is what makes it usable as
 * encryption key material at all.
 * @param {Uint8Array} credentialId
 * @returns {Promise<Uint8Array | null>} null if the ceremony didn't
 *   return a usable PRF value (cancelled, unsupported, etc.)
 */
export async function getPrfSecret (credentialId) {
  if (!isWebAuthnAvailable()) return null

  try {
    const challenge = crypto.getRandomValues(new Uint8Array(32))

    const assertion = await navigator.credentials.get({
      publicKey: {
        challenge,
        allowCredentials: [{ id: credentialId, type: 'public-key' }],
        userVerification: 'required',
        extensions: { prf: { eval: { first: PRF_SALT } } }
      }
    })

    const extResults = assertion?.getClientExtensionResults()
    const result = extResults?.prf?.results?.first
    return result ? new Uint8Array(result) : null
  } catch {
    // Same reasoning as setupPrfCredential's catch — a thrown exception
    // here (origin restrictions, user cancellation, authenticator
    // removed/reset) must not crash the caller; null is this function's
    // documented "couldn't get a usable value" signal either way.
    return null
  }
}
